<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';

import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import useField from '@/composables/useField';
import { fetchGetLookupSuburb } from '@/helpers/fetchers';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export interface Props {
  modelValue?: openapi.SuburbOpenapi | null;
  size?: FieldSize;
  fetchItems?: (
    params: openapi.AddressBookSuburbLookupListQueryOpenapi,
  ) => Promise<openapi.AddressBookSuburbLookupListContentOpenapi>;
  loading?: boolean;
  minSearchLength?: number;
  readonly?: boolean;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'md',
  fetchItems: fetchGetLookupSuburb,
  loading: false,
  minSearchLength: 3,
  readonly: false,
  disabled: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const search = ref('');
const field = ref<HTMLFormElement | null>(null);

const items = ref<openapi.SuburbOpenapi[]>([]);
const isFetching = ref(false);

function fetchWrapper(): void {
  const searchPattern = search.value;
  isFetching.value = true;
  void props
    .fetchItems({ suburb_guess: searchPattern })
    .then((fetchedItems) => {
      if (searchPattern !== search.value) return;
      items.value = fetchedItems;
    })
    .finally(() => {
      isFetching.value = false;
    });
}

const debounceFetchWrapper = useDebounceFn(fetchWrapper, 150);

function getSearchString(search?: string | null): string {
  return (search?.trim() || '').toUpperCase();
}

function getSearchStringFromValue(
  value?: openapi.SuburbOpenapi | null,
): string {
  let search = '';
  if (value?.name) search = value.name;
  return getSearchString(search);
}

watch(search, (newSearch?: string | null, oldSearch?: string | null) => {
  // console.debug('fieldValue.value', fieldValue.value);
  // console.debug('newSearch', newSearch);
  // console.debug('oldSearch', oldSearch);
  if (newSearch === null && fieldValue.value) {
    fieldValue.value = null;
    return;
  }
  newSearch = getSearchString(newSearch);
  oldSearch = getSearchString(oldSearch);
  const value = getSearchStringFromValue(fieldValue.value);
  if (newSearch.length >= props.minSearchLength) {
    if (newSearch !== oldSearch) {
      if (newSearch !== value || oldSearch !== '') {
        void debounceFetchWrapper();
      }
    }
    // } else if (newSearch === '' && oldSearch === value) {
  } else {
    items.value = [];
  }
});

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});
const { handleEnterKeydown } = useAutocompleteField(fieldValue, search, field);

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-autocomplete
    ref="field"
    v-model="fieldValue"
    v-model:search="search"
    label="Suburb"
    :items="items"
    item-title="name"
    item-value="id"
    return-object
    :class="[
      'g-v-text-field',
      $style.suburbAutocompleteField,
      $style[sizeClass],
      fieldStyles[sizeClass],
    ]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    :clearable="!props.readonly"
    :menu-icon="props.readonly ? '' : 'mdi-chevron-down'"
    :loading="props.loading || isFetching"
    :readonly="props.readonly"
    :disabled="props.disabled"
    no-filter
    data-testid="suburbAutocompleteField"
    @keydown.enter="handleEnterKeydown"
  >
    <template #no-data>
      <NoDataSelectVListItem>
        <template v-if="!search || search.length < props.minSearchLength">
          Please enter {{ props.minSearchLength }} or more characters
        </template>
        <template v-else>No such data</template>
      </NoDataSelectVListItem>
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        :ripple="false"
        v-bind="itemData.props"
      >
        <template #title>
          {{ itemData.item.raw.name }}, {{ itemData.item.raw.state }},
          {{ itemData.item.raw.postcode }}
        </template>
      </v-list-item>
    </template>
    <template #prepend="prependData">
      <template
        v-if="fieldValue && fieldValue.name && fieldValue.state && !field?.menu"
      >
        {{ fieldValue.state }}, {{ fieldValue.postcode }}
      </template>
    </template>
  </v-autocomplete>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.suburbAutocompleteField {
  position: relative;

  :global {
    .v-input__prepend {
      position: absolute;
      z-index: 1;
      left: 16px;
      font-size: 9px;
      line-height: 1;
      white-space: nowrap;
    }
    .v-autocomplete__selection {
      margin-top: 0;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.size-xs {
  :global {
    .v-input__prepend {
      top: 26px;
    }
  }
}

.size-sm {
  :global {
    .v-input__prepend {
      top: 30px;
    }
  }
}

.size-md {
  :global {
    .v-input__prepend {
      top: 30px;
    }
  }
}

.size-lg {
  :global {
    .v-input__prepend {
      top: 38px;
    }
  }
}
</style>
